import { Trophy, Medal, Award, TrendingUp, Zap, Star } from 'lucide-react';

interface LeaderboardScreenProps {
  onBack: () => void;
}

export function LeaderboardScreen({ onBack }: LeaderboardScreenProps) {
  const currentUser = {
    rank: 24,
    name: 'You',
    deliveries: 52,
    rating: 4.8,
    earnings: 26400,
  };

  const topRiders = [
    {
      rank: 1,
      name: 'Chukwudi O.',
      deliveries: 127,
      rating: 5.0,
      earnings: 89500,
      badge: 'Speed King',
      avatar: '👑',
    },
    {
      rank: 2,
      name: 'Amina K.',
      deliveries: 118,
      rating: 4.9,
      earnings: 82300,
      badge: 'Top Rated',
      avatar: '⭐',
    },
    {
      rank: 3,
      name: 'Tunde A.',
      deliveries: 112,
      rating: 4.9,
      earnings: 78900,
      badge: 'Consistent',
      avatar: '🏆',
    },
    {
      rank: 4,
      name: 'Ngozi M.',
      deliveries: 98,
      rating: 4.8,
      earnings: 68200,
    },
    {
      rank: 5,
      name: 'Ibrahim S.',
      deliveries: 94,
      rating: 4.8,
      earnings: 65100,
    },
    {
      rank: 6,
      name: 'Fatima B.',
      deliveries: 89,
      rating: 4.7,
      earnings: 61500,
    },
    {
      rank: 7,
      name: 'Emeka N.',
      deliveries: 84,
      rating: 4.8,
      earnings: 58900,
    },
    {
      rank: 8,
      name: 'Aisha L.',
      deliveries: 79,
      rating: 4.7,
      earnings: 55200,
    },
    {
      rank: 9,
      name: 'David O.',
      deliveries: 73,
      rating: 4.6,
      earnings: 51800,
    },
    {
      rank: 10,
      name: 'Blessing U.',
      deliveries: 68,
      rating: 4.7,
      earnings: 48300,
    },
  ];

  const getRankIcon = (rank: number) => {
    if (rank === 1) return { icon: Trophy, color: 'text-yellow-500', bg: 'bg-yellow-100' };
    if (rank === 2) return { icon: Medal, color: 'text-gray-400', bg: 'bg-gray-100' };
    if (rank === 3) return { icon: Medal, color: 'text-orange-600', bg: 'bg-orange-100' };
    return { icon: Award, color: 'text-gray-400', bg: 'bg-gray-100' };
  };

  return (
    <div className="fixed inset-0 bg-white flex flex-col">
      {/* Header */}
      <div className="p-8 pt-16 bg-gradient-to-br from-yellow-500/20 via-orange-500/20 to-red-500/20">
        <button
          onClick={onBack}
          className="mb-6 text-[#E0001B] hover:underline"
        >
          ← Back
        </button>

        <div className="flex items-center gap-4 mb-6">
          <div className="w-16 h-16 rounded-full bg-gradient-to-br from-yellow-400 to-orange-500 flex items-center justify-center shadow-lg">
            <Trophy className="w-8 h-8 text-white" />
          </div>
          <div>
            <h1 className="text-[28px] text-[#111111] mb-1">
              Leaderboard
            </h1>
            <p className="text-[#6B6B6B]">This week's top performers</p>
          </div>
        </div>

        {/* Your Rank Card */}
        <div className="p-6 bg-white rounded-[16px] shadow-sm border-2 border-[#E0001B]">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#E0001B] to-[#C00017] flex items-center justify-center text-white text-[18px] font-bold">
                #{currentUser.rank}
              </div>
              <div>
                <p className="text-[18px] font-bold text-[#111111]">{currentUser.name}</p>
                <p className="text-[14px] text-[#6B6B6B]">Your current rank</p>
              </div>
            </div>
            <TrendingUp className="w-6 h-6 text-green-600" />
          </div>
          <div className="grid grid-cols-3 gap-3 mt-4 pt-4 border-t border-gray-200">
            <div className="text-center">
              <p className="text-[20px] font-bold text-[#E0001B]">{currentUser.deliveries}</p>
              <p className="text-[12px] text-[#6B6B6B]">Deliveries</p>
            </div>
            <div className="text-center">
              <p className="text-[20px] font-bold text-yellow-500">{currentUser.rating}</p>
              <p className="text-[12px] text-[#6B6B6B]">Rating</p>
            </div>
            <div className="text-center">
              <p className="text-[20px] font-bold text-green-600">₦{(currentUser.earnings / 1000).toFixed(0)}k</p>
              <p className="text-[12px] text-[#6B6B6B]">Earnings</p>
            </div>
          </div>
        </div>
      </div>

      {/* Leaderboard List */}
      <div className="flex-1 overflow-auto px-8 py-6">
        {/* Top 3 Podium */}
        <div className="mb-6">
          <h2 className="text-[18px] font-medium mb-4">🏆 Top 3 This Week</h2>
          <div className="flex items-end justify-center gap-4 mb-6">
            {/* 2nd Place */}
            <div className="flex-1 pb-4">
              <div className="bg-gradient-to-b from-gray-200 to-gray-300 rounded-t-[12px] p-4 text-center">
                <div className="w-16 h-16 rounded-full bg-white flex items-center justify-center text-[24px] mx-auto mb-2 shadow-md">
                  {topRiders[1].avatar}
                </div>
                <p className="text-[16px] font-bold mb-1">{topRiders[1].name}</p>
                <div className="flex items-center justify-center gap-1 mb-2">
                  <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                  <span className="text-[14px]">{topRiders[1].rating}</span>
                </div>
                <p className="text-[12px] text-gray-600">{topRiders[1].deliveries} rides</p>
              </div>
              <div className="h-20 bg-gray-300 rounded-b-[12px] flex items-center justify-center">
                <span className="text-[32px] font-bold text-gray-500">2</span>
              </div>
            </div>

            {/* 1st Place */}
            <div className="flex-1">
              <div className="bg-gradient-to-b from-yellow-300 to-yellow-400 rounded-t-[12px] p-4 text-center">
                <div className="w-20 h-20 rounded-full bg-white flex items-center justify-center text-[32px] mx-auto mb-2 shadow-lg">
                  {topRiders[0].avatar}
                </div>
                <p className="text-[18px] font-bold mb-1">{topRiders[0].name}</p>
                <div className="flex items-center justify-center gap-1 mb-2">
                  <Star className="w-5 h-5 fill-yellow-600 text-yellow-600" />
                  <span className="text-[16px] font-bold">{topRiders[0].rating}</span>
                </div>
                <p className="text-[14px] text-yellow-800">{topRiders[0].deliveries} rides</p>
                <span className="inline-block mt-2 px-3 py-1 bg-yellow-600 text-white text-[12px] rounded-full">
                  {topRiders[0].badge}
                </span>
              </div>
              <div className="h-32 bg-yellow-400 rounded-b-[12px] flex items-center justify-center">
                <span className="text-[48px] font-bold text-yellow-700">1</span>
              </div>
            </div>

            {/* 3rd Place */}
            <div className="flex-1 pb-8">
              <div className="bg-gradient-to-b from-orange-200 to-orange-300 rounded-t-[12px] p-4 text-center">
                <div className="w-16 h-16 rounded-full bg-white flex items-center justify-center text-[24px] mx-auto mb-2 shadow-md">
                  {topRiders[2].avatar}
                </div>
                <p className="text-[16px] font-bold mb-1">{topRiders[2].name}</p>
                <div className="flex items-center justify-center gap-1 mb-2">
                  <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                  <span className="text-[14px]">{topRiders[2].rating}</span>
                </div>
                <p className="text-[12px] text-orange-700">{topRiders[2].deliveries} rides</p>
              </div>
              <div className="h-16 bg-orange-300 rounded-b-[12px] flex items-center justify-center">
                <span className="text-[28px] font-bold text-orange-600">3</span>
              </div>
            </div>
          </div>
        </div>

        {/* Full Rankings */}
        <div>
          <h2 className="text-[18px] font-medium mb-4">All Rankings</h2>
          <div className="space-y-2">
            {topRiders.map(rider => {
              const rankStyle = getRankIcon(rider.rank);
              const Icon = rankStyle.icon;
              
              return (
                <div
                  key={rider.rank}
                  className={`p-4 rounded-[12px] border transition-colors ${
                    rider.rank <= 3
                      ? 'bg-gradient-to-r from-yellow-50 to-orange-50 border-yellow-200'
                      : 'bg-white border-gray-200'
                  }`}
                >
                  <div className="flex items-center gap-4">
                    <div className={`w-10 h-10 rounded-full ${rankStyle.bg} flex items-center justify-center flex-shrink-0`}>
                      {rider.rank <= 3 ? (
                        <Icon className={`w-5 h-5 ${rankStyle.color}`} />
                      ) : (
                        <span className="text-[14px] font-bold text-[#6B6B6B]">#{rider.rank}</span>
                      )}
                    </div>
                    
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <p className="text-[16px] font-medium">{rider.name}</p>
                        {rider.badge && (
                          <span className="px-2 py-0.5 bg-[#E0001B]/10 text-[#E0001B] text-[10px] rounded-full">
                            {rider.badge}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-4 text-[12px] text-[#6B6B6B]">
                        <span>{rider.deliveries} rides</span>
                        <span className="flex items-center gap-1">
                          <Star className="w-3 h-3 fill-yellow-400 text-yellow-400" />
                          {rider.rating}
                        </span>
                        <span className="text-green-600 font-medium">₦{(rider.earnings / 1000).toFixed(0)}k</span>
                      </div>
                    </div>

                    <Zap className={`w-5 h-5 ${rider.rank <= 3 ? 'text-yellow-500' : 'text-gray-400'}`} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Info Card */}
        <div className="mt-6 p-6 bg-blue-50 rounded-[12px] border border-blue-100">
          <h3 className="text-[16px] font-medium text-blue-900 mb-2">How Rankings Work</h3>
          <ul className="text-[14px] text-blue-800 space-y-1">
            <li>• Rankings reset every Monday at 12:00 AM</li>
            <li>• Based on deliveries completed, rating, and earnings</li>
            <li>• Top 10 riders receive bonus rewards</li>
            <li>• #1 rider gets featured on social media</li>
          </ul>
        </div>
      </div>
    </div>
  );
}
